import React, { FC, useState, useEffect } from 'react'
import { Controller, ControllerProps } from 'react-hook-form'
import debounce from 'lodash/debounce'
import { useCreateLoadFormContext } from 'apps/fleet-planner/context'
import { ILoadEquipment } from 'apps/fleet-planner/types'
import { IEquipmentProps, Equipment } from './Equipment'

type IBaseControllerProps = ControllerProps<React.ComponentType<IEquipmentProps>>

interface IEquipmentState
  extends Pick<ILoadEquipment, 'trailerType' | 'trailerLength' | 'temperatureSetting'> {}
export interface IControlledCustomerProps
  extends Pick<IBaseControllerProps, 'control' | 'name' | 'defaultValue'> {
  /**
   * Adds a slight delay to updating the values in `control` object
   * This significantly improves performance on large forms, and forms
   * that use `useFormContext` hook
   * @default 150
   */
  debounceMs?: number
  EquipmentProps?: Partial<IEquipmentProps>
  defaultValue?: IEquipmentState
}
/**
 * Helper component for use with React Hook Form
 * @see https://react-hook-form.com/api#Controller
 * @example
 * ```
 * <ControlledEquipment
 *   name='equipment'
 *   control={control}
 * />
 * ```
 */

export const ControlledEquipment: FC<IControlledCustomerProps> = ({
  control,
  defaultValue = {},
  EquipmentProps = {},
  debounceMs = 150,
}) => {
  const { large = true } = EquipmentProps || {}
  const { setValue } = useCreateLoadFormContext()
  const [equipment, setEquipment] = useState<IEquipmentState>({ ...defaultValue })
  const changeHandler = debounce((change: Partial<ILoadEquipment>) => {
    setEquipment(prev => ({
      ...prev,
      ...change,
    }))
  }, debounceMs)

  const { temperatureSetting, trailerLength, trailerType } = equipment || {}

  useEffect(() => {
    setValue('trailerType', trailerType || '')
  }, [trailerType, setValue])

  useEffect(() => {
    setValue('trailerLength', trailerLength || '')
  }, [trailerLength, setValue])

  useEffect(() => {
    setValue('temperatureSetting', temperatureSetting)
  }, [temperatureSetting, setValue])

  return (
    <>
      <Controller
        name='trailerType'
        control={control}
        defaultValue={{}}
        render={({ value }) => <input type='hidden' value={value} />}
      />
      <Controller
        name='trailerLength'
        control={control}
        defaultValue={{}}
        render={({ value }) => <input type='hidden' value={value} />}
      />
      <Controller
        name='temperatureSetting'
        control={control}
        defaultValue=''
        render={({ value }) => <input type='hidden' value={value} />}
      />
      <Equipment
        trailerType={trailerType}
        trailerLength={trailerLength}
        temperatureSetting={temperatureSetting}
        onChange={changeHandler}
        large={large}
      />
    </>
  )
}
